import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountingPeriodSection } from '../components/AccountingPeriodSection';
import { AppShell } from '../components/AppShell';
import { BanksSection } from '../components/BanksSection';
import { BusinessTravelSection } from '../components/BusinessTravelSection';
import { ChartOfAccountsSection } from '../components/ChartOfAccountsSection';
import { CompanyInfoSection } from '../components/CompanyInfoSection';
import { InitialBalanceSection } from '../components/InitialBalanceSection';
import { SalaryFormulaSection } from '../components/SalaryFormulaSection';

type SettingsTab = 'accounts' | 'balance' | 'period' | 'salary' | 'banks' | 'company' | 'congtac';

interface TabDef {
  key: SettingsTab;
  label: string;
  icon: JSX.Element;
}

/** Metadata theo prototype — mỗi tab có header banner riêng. */
const TABS: TabDef[] = [
  {
    key: 'accounts',
    label: 'Hệ thống tài khoản',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>
    ),
  },
  {
    key: 'balance',
    label: 'Số dư đầu kỳ',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
    ),
  },
  {
    key: 'period',
    label: 'Kỳ kế toán',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
    ),
  },
  {
    key: 'salary',
    label: 'Chi phí lương',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /></svg>
    ),
  },
  {
    key: 'banks',
    label: 'Ngân hàng',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 21 18 0" /><path d="M3 10h18" /><path d="M5 6 12 3l7 3" /><path d="M4 10v11" /><path d="M20 10v11" /><path d="M8 14v3" /><path d="M12 14v3" /><path d="M16 14v3" /></svg>
    ),
  },
  {
    key: 'company',
    label: 'Công ty',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M10 6h4M10 10h4M10 14h4M10 18h4" /></svg>
    ),
  },
  {
    key: 'congtac',
    label: 'Chính sách công tác',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="7" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
    ),
  },
];

export function SettingsPage() {
  const [sp, setSp] = useSearchParams();
  const initial = (sp.get('tab') as SettingsTab) ?? 'accounts';
  const [tab, setTabState] = useState<SettingsTab>(initial);
  const setTab = (t: SettingsTab) => {
    setTabState(t);
    setSp((prev) => { const n = new URLSearchParams(prev); n.set('tab', t); return n; }, { replace: true });
  };

  return (
    <AppShell title="Cài đặt" subtitle="Cấu hình hệ thống kế toán">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Tab bar — pill group matching prototype */}
        <div style={tabBarStyle}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              style={tabBtnStyle(tab === t.key)}
              onClick={() => setTab(t.key)}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Section content */}
        {/* Mỗi section trả về nhiều block top-level; giữ nhịp 16px như prototype. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {tab === 'accounts' && <ChartOfAccountsSection />}
          {tab === 'balance' && <InitialBalanceSection onGoToAccountsTab={() => setTab('accounts')} />}
          {tab === 'period' && <AccountingPeriodSection />}
          {tab === 'salary' && <SalaryFormulaSection />}
          {tab === 'banks' && <BanksSection />}
          {tab === 'company' && <CompanyInfoSection />}
          {tab === 'congtac' && <BusinessTravelSection />}
        </div>
      </div>
    </AppShell>
  );
}

// ==== Tab bar styles matching prototype ====

const tabBarStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: '#f8fafc',
  border: '1px solid #eef2f7',
  borderRadius: 9999,
  padding: 6,
  flexWrap: 'wrap',
  alignSelf: 'flex-start',
};

function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: active ? '#fff' : 'transparent',
    color: active ? '#172033' : '#64748b',
    fontSize: 13,
    fontWeight: 600,
    border: active ? '1px solid #e4e7ec' : '1px solid transparent',
    borderRadius: 9999,
    cursor: 'pointer',
    boxShadow: active ? '0 1px 2px rgba(15,23,42,.05)' : 'none',
    whiteSpace: 'nowrap',
  };
}
